// src/components/TrainingEnrollForm.js
import React, { useState } from 'react';
import { Send, CheckCircle, AlertCircle } from 'lucide-react';
import '../styles/TrainingEnrollForm.css';
import { sendContactEmail } from '../services/emailService';
import { trainingPrograms, eligibleDegrees } from '../data/trainingData';

const TrainingEnrollForm = ({ defaultProgram = '', defaultDuration = '6 Weeks' }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    college: '',
    degree: '',
    program: defaultProgram,
    duration: defaultDuration,
    message: ''
  });

  const [formStatus, setFormStatus] = useState({
    loading: false,
    submitted: false,
    error: false,
    message: ''
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name || !formData.email || !formData.college || !formData.degree || !formData.program) {
      setFormStatus({
        loading: false,
        submitted: false,
        error: true,
        message: 'Please fill in all required fields.'
      });
      return;
    }

    setFormStatus({ loading: true, submitted: false, error: false, message: '' });

    try {
      const result = await sendContactEmail(
        {
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          company: `${formData.college} — ${formData.degree}`,
          service: `${formData.program} (${formData.duration})`,
          message: formData.message || 'No additional message provided.'
        },
        'training'
      );

      if (result.success) {
        setFormStatus({
          loading: false,
          submitted: true,
          error: false,
          message: 'Thank you for applying! Our team will reach out to you within 24 hours with batch and fee details.'
        });

        setFormData({
          name: '',
          email: '',
          phone: '',
          college: '',
          degree: '',
          program: defaultProgram,
          duration: defaultDuration,
          message: ''
        });

        setTimeout(() => {
          setFormStatus({ loading: false, submitted: false, error: false, message: '' });
        }, 8000);
      }
    } catch (error) {
      setFormStatus({
        loading: false,
        submitted: false,
        error: true,
        message: error.message || 'Failed to send application. Please try again or email us at info@edsytech.com'
      });

      setTimeout(() => {
        setFormStatus({ loading: false, submitted: false, error: false, message: '' });
      }, 8000);
    }
  };

  return (
    <div className="enroll-form-card" id="apply">
      <h3 className="enroll-form-title">Apply for Training / Internship</h3>
      <p className="enroll-form-subtitle">
        Fill out the form below and our team will contact you with batch dates and fee details.
      </p>

      {formStatus.submitted && (
        <div className="enroll-alert enroll-alert-success">
          <CheckCircle className="enroll-alert-icon" />
          <p>{formStatus.message}</p>
        </div>
      )}

      {formStatus.error && (
        <div className="enroll-alert enroll-alert-error">
          <AlertCircle className="enroll-alert-icon" />
          <p>{formStatus.message}</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="enroll-form">
        <div className="enroll-form-row">
          <div className="enroll-form-group">
            <label htmlFor="name" className="enroll-form-label">
              Full Name <span className="enroll-required">*</span>
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              className="enroll-form-input"
              placeholder="Your full name"
              required
            />
          </div>

          <div className="enroll-form-group">
            <label htmlFor="email" className="enroll-form-label">
              Email Address <span className="enroll-required">*</span>
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className="enroll-form-input"
              placeholder="you@example.com"
              required
            />
          </div>
        </div>

        <div className="enroll-form-row">
          <div className="enroll-form-group">
            <label htmlFor="phone" className="enroll-form-label">
              Phone Number
            </label>
            <input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              className="enroll-form-input"
              placeholder="+91 98765 43210"
            />
          </div>

          <div className="enroll-form-group">
            <label htmlFor="college" className="enroll-form-label">
              College / University <span className="enroll-required">*</span>
            </label>
            <input
              type="text"
              id="college"
              name="college"
              value={formData.college}
              onChange={handleChange}
              className="enroll-form-input"
              placeholder="Your college name"
              required
            />
          </div>
        </div>

        <div className="enroll-form-row">
          <div className="enroll-form-group">
            <label htmlFor="degree" className="enroll-form-label">
              Degree Program <span className="enroll-required">*</span>
            </label>
            <select
              id="degree"
              name="degree"
              value={formData.degree}
              onChange={handleChange}
              className="enroll-form-select"
              required
            >
              <option value="">Select your degree</option>
              {eligibleDegrees.map((degree) => (
                <option key={degree} value={degree}>
                  {degree}
                </option>
              ))}
              <option value="Other">Other</option>
            </select>
          </div>

          <div className="enroll-form-group">
            <label htmlFor="duration" className="enroll-form-label">
              Preferred Duration <span className="enroll-required">*</span>
            </label>
            <select
              id="duration"
              name="duration"
              value={formData.duration}
              onChange={handleChange}
              className="enroll-form-select"
              required
            >
              <option value="6 Weeks">6 Weeks</option>
              <option value="6 Months">6 Months</option>
            </select>
          </div>
        </div>

        <div className="enroll-form-group">
          <label htmlFor="program" className="enroll-form-label">
            Training Program <span className="enroll-required">*</span>
          </label>
          <select
            id="program"
            name="program"
            value={formData.program}
            onChange={handleChange}
            className="enroll-form-select"
            required
          >
            <option value="">Select a program</option>
            {trainingPrograms.map((program) => (
              <option key={program.slug} value={program.title}>
                {program.title}
              </option>
            ))}
          </select>
        </div>

        <div className="enroll-form-group">
          <label htmlFor="message" className="enroll-form-label">
            Message (optional)
          </label>
          <textarea
            id="message"
            name="message"
            value={formData.message}
            onChange={handleChange}
            className="enroll-form-textarea"
            placeholder="Anything you'd like us to know?"
            rows="4"
          ></textarea>
        </div>

        <button type="submit" className="enroll-submit-button" disabled={formStatus.loading}>
          <Send className="enroll-submit-icon" />
          {formStatus.loading ? 'Submitting...' : 'Submit Application'}
        </button>
      </form>
    </div>
  );
};

export default TrainingEnrollForm;
